import { PhUploadKind, validateUploadFile } from '@malash-office/types';

/**
 * Sorting a dropped batch into "these can be uploaded" and "these cannot, and here is why".
 *
 * The rules themselves are **not** here — they live in `libs/types` and the server applies the
 * same function to the same file. This module only handles the part the server never sees: a user
 * dropping twelve files at once, three of which are wrong.
 *
 * Rejecting the whole batch because one file is a `.mov` would be the easy implementation and the
 * wrong one; the nine good images upload and the three failures are reported by name.
 */

export interface MediaValidationResult {
  accepted: File[];
  /** One Hebrew sentence per rejected file, already naming the file. */
  errors: string[];
}

export const validateFiles = (files: File[], kind: PhUploadKind): MediaValidationResult => {
  const accepted: File[] = [];
  const errors: string[] = [];

  files.forEach(file => {
    const rejection = validateUploadFile({ name: file.name, size: file.size, type: file.type }, kind);
    if (rejection) errors.push(`${file.name}: ${rejection.message}`);
    else accepted.push(file);
  });

  return { accepted, errors };
};

/** `FileList` from an input or a drop, as an array. */
export const toFileArray = (files: FileList | null): File[] => (files ? Array.from(files) : []);

/**
 * A human file size, for the document list.
 *
 * `KB`/`MB` only: a product document is never small enough for bytes to be informative, and never
 * large enough for GB.
 */
export const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '';
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};
